import { Capacitor } from "@capacitor/core";
import {
  ConsentStatus,
  ConsentType,
  FirebaseAnalytics,
} from "@capacitor-firebase/analytics";

const CONSENT_STORAGE_KEY = "fp:analytics-consent:v1";
const ACTIVATION_STORAGE_KEY = "fp:analytics-activated:v1";
export const ANALYTICS_CONSENT_EVENT = "fp:analytics-consent-changed";

const ALLOWED_EVENTS = new Set([
  "login",
  "sign_up",
  "family_created",
  "family_joined",
  "activated_user",
  "core_action",
]);

const ALLOWED_PARAMS = Object.freeze({
  login: new Set(["method"]),
  sign_up: new Set(["method"]),
  family_created: new Set(["source"]),
  family_joined: new Set(["source"]),
  activated_user: new Set(["activation_source"]),
  core_action: new Set(["feature"]),
});

const SAFE_PARAM_VALUE = /^[a-z0-9_]{1,40}$/;
let initializationPromise = null;

const safeStorage = {
  get(key) {
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value); } catch {}
  },
  remove(key) {
    try { window.localStorage.removeItem(key); } catch {}
  },
};

export const getAnalyticsConsent = () => {
  const raw = safeStorage.get(CONSENT_STORAGE_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (typeof value?.analytics !== "boolean" || typeof value?.ads !== "boolean") return null;
    return { analytics: value.analytics, ads: value.ads, decidedAt: value.decidedAt || null };
  } catch {
    return null;
  }
};

const enforcePlatformPrivacy = (consent) => {
  const analytics = Boolean(consent?.analytics);
  return {
    analytics,
    // iOS ölçümü IDFA ve çapraz uygulama/site takibi olmadan çalışır.
    // Reklam ilişkilendirmesi Apple'ın gizlilik odaklı attribution
    // mekanizmaları ve Google'ın izin durumuna saygılı modellemesiyle yapılır.
    ads: Capacitor.getPlatform() === "ios" ? false : Boolean(analytics && consent?.ads),
    decidedAt: consent?.decidedAt || null,
  };
};

export const getEffectiveAnalyticsConsent = () => enforcePlatformPrivacy(
  getAnalyticsConsent() || (Capacitor.isNativePlatform()
    ? { analytics: true, ads: true, decidedAt: null }
    : { analytics: false, ads: false, decidedAt: null }),
);

const applyConsentToSdk = async ({ analytics, ads }) => {
  const allowAdsMeasurement = Boolean(analytics && ads && Capacitor.getPlatform() !== "ios");
  const granted = ConsentStatus.Granted;
  const denied = ConsentStatus.Denied;
  const statuses = [
    [ConsentType.AnalyticsStorage, analytics ? granted : denied],
    [ConsentType.AdStorage, allowAdsMeasurement ? granted : denied],
    [ConsentType.AdUserData, allowAdsMeasurement ? granted : denied],
    // Aile/çocuk bağlamında davranışsal reklam profili oluşturmuyoruz.
    [ConsentType.AdPersonalization, denied],
    [ConsentType.PersonalizationStorage, denied],
    [ConsentType.FunctionalityStorage, granted],
  ];

  for (const [type, status] of statuses) {
    await FirebaseAnalytics.setConsent({ type, status });
  }
  await FirebaseAnalytics.setEnabled({ enabled: analytics });

  if (!analytics && Capacitor.isNativePlatform()) {
    await FirebaseAnalytics.resetAnalyticsData().catch(() => {});
  }
};

export const initializeAnalytics = () => {
  if (initializationPromise) return initializationPromise;
  initializationPromise = (async () => {
    const consent = getEffectiveAnalyticsConsent();
    try {
      await applyConsentToSdk(consent);
    } catch {
      // Analytics hiçbir zaman uygulamanın temel işlevlerini engellememelidir.
    }
    return consent;
  })();
  return initializationPromise;
};

export const updateAnalyticsConsent = async ({ analytics, ads }) => {
  const next = enforcePlatformPrivacy({
    analytics: Boolean(analytics),
    ads: Boolean(analytics && ads),
    decidedAt: new Date().toISOString(),
  });
  safeStorage.set(CONSENT_STORAGE_KEY, JSON.stringify(next));
  await applyConsentToSdk(next).catch(() => {});
  window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_EVENT, { detail: next }));
  return next;
};

const sanitizeParams = (eventName, params = {}) => {
  const allowed = ALLOWED_PARAMS[eventName] || new Set();
  return Object.entries(params).reduce((result, [key, value]) => {
    const normalized = String(value ?? "").trim().toLowerCase();
    if (allowed.has(key) && SAFE_PARAM_VALUE.test(normalized)) result[key] = normalized;
    return result;
  }, {});
};

export const logAnalyticsEvent = async (name, params = {}) => {
  const eventName = String(name || "").trim();
  if (!ALLOWED_EVENTS.has(eventName) || !getEffectiveAnalyticsConsent().analytics) return false;
  try {
    await initializeAnalytics();
    await FirebaseAnalytics.logEvent({ name: eventName, params: sanitizeParams(eventName, params) });
    return true;
  } catch {
    return false;
  }
};

export const trackScreenView = async (screenName) => {
  if (!getEffectiveAnalyticsConsent().analytics) return false;
  const safeName = String(screenName || "").trim().toLowerCase();
  if (!SAFE_PARAM_VALUE.test(safeName)) return false;
  try {
    await initializeAnalytics();
    await FirebaseAnalytics.setCurrentScreen({ screenName: safeName });
    return true;
  } catch {
    return false;
  }
};

export const trackCoreAction = async (feature) => {
  await logAnalyticsEvent("core_action", { feature });
  if (safeStorage.get(ACTIVATION_STORAGE_KEY) === "1") return;
  const sent = await logAnalyticsEvent("activated_user", { activation_source: feature });
  if (sent) safeStorage.set(ACTIVATION_STORAGE_KEY, "1");
};

export const clearAnalyticsState = async () => {
  safeStorage.remove(ACTIVATION_STORAGE_KEY);
  await FirebaseAnalytics.resetAnalyticsData().catch(() => {});
};
